import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faStickyNote, faCheckCircle, faInfoCircle, faSpinner, faPlus, faTag } from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import { nextNoteId } from '../utils/orbitStakeholderNotes';
import './AddNotePage.css';

interface AddNotePageProps {
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
  onNoteAdded?: () => void;
  existingTags?: string[];
}

const AddNotePage: React.FC<AddNotePageProps> = ({ 
  onClose, 
  stakeholderId, 
  type,
  stakeholderData,
  onNoteAdded,
  existingTags = []
}) => {
  const [noteText, setNoteText] = useState<string>('');
  const [tagInput, setTagInput] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const employeeId = useActiveEmployeeId();

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(e.target.value);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const trimmedTag = tagInput.trim();
      if (!tags.includes(trimmedTag)) {
        setTags([...tags, trimmedTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Filter suggestions based on input
  const tagSuggestions = existingTags
    .filter(tag => 
      !tags.includes(tag) && 
      tag.toLowerCase().includes(tagInput.toLowerCase().trim())
    )
    .slice(0, 5); // Limit to 5 suggestions

  // Show available tags that aren't yet selected (for quick add)
  const availableTags = existingTags.filter(tag => !tags.includes(tag));

  const addTagFromSuggestion = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setTagInput('');
  };

  const handleClose = () => {
    setNoteText('');
    setTags([]);
    setTagInput('');
    setError('');
    onClose();
  };

  const handleNoteCreation = async () => {
    setLoading(true);
    setError('');

    if (!noteText.trim()) {
      setLoading(false);
      setError('Please enter a note.');
      return;
    }

    if (!employeeId) {
      setLoading(false);
      setError('Unable to identify user. Please refresh the page and try again.');
      return;
    }

    try {
      // Resolve client vs agent: try client first, then agent
      let clientData: any = null;
      let agentData: any = null;
      try {
        clientData = await ClientService.getClientById(stakeholderId);
      } catch {
        clientData = null;
      }
      if (!clientData) {
        try {
          agentData = await AgentsService.getAgent(stakeholderId);
        } catch {
          agentData = null;
        }
      }

      if (clientData) {
        // --- Client path ---
        let existingNotes: any[] = [];
        const notesField = (clientData as any).notes ?? (clientData as any).Notes;

        if (notesField) {
          try {
            const notesData = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
            if (Array.isArray(notesData)) {
              existingNotes = notesData;
            } else if (typeof notesData === 'object' && notesData !== null) {
              existingNotes = [notesData];
            }
          } catch (error) {
            console.error('Error parsing existing notes:', error);
            existingNotes = [];
          }
        }

        const newNote = {
          noteid: nextNoteId(existingNotes),
          note_text: noteText.trim(),
          tags: tags,
          created_at: new Date().toISOString(),
          created_by: employeeId,
        };
        existingNotes.push(newNote);

        const getFieldValue = (field: string, isDateField: boolean = false) => {
          const snakeValue = (clientData as any)[field];
          const pascalKey = field.split('_').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
          const capitalizedKey = field.charAt(0).toUpperCase() + field.slice(1);
          const pascalValue = (clientData as any)[pascalKey];
          const capitalizedValue = (clientData as any)[capitalizedKey];
          const value = snakeValue !== undefined && snakeValue !== null
            ? snakeValue
            : pascalValue !== undefined && pascalValue !== null
            ? pascalValue
            : capitalizedValue;

          if (isDateField) {
            if (value === '' || value === null || value === undefined) {
              return null;
            }
          }
          return value !== null && value !== undefined ? value : '';
        };

        const updateData: any = {
          FirstName: getFieldValue('first_name'),
          LastName: getFieldValue('last_name'),
          Company: getFieldValue('company'),
          Email: getFieldValue('email'),
          Phone: getFieldValue('phone'),
          Address: getFieldValue('address'),
          Type: getFieldValue('type'),
          ContactName: getFieldValue('contact_name'),
          Prefix: getFieldValue('prefix'),
          MiddleName: getFieldValue('middle_name'),
          DateOfBirth: getFieldValue('date_of_birth', true),
          SecondaryPhoneNumber: getFieldValue('secondary_phone_number'),
          Entity: getFieldValue('entity'),
          Title: getFieldValue('title'),
          ContactCreatedDate: getFieldValue('contact_created_date', true),
          PrimaryAddressCity: getFieldValue('primary_address_city'),
          PrimaryAddressProvince: getFieldValue('primary_address_province'),
          PrimaryAddressPostalZip: getFieldValue('primary_address_postal_zip'),
          PrimaryAddressCountry: getFieldValue('primary_address_country'),
          SecondaryEmailAddress: getFieldValue('secondary_email_address'),
          SecondaryAddressStreet: getFieldValue('secondary_address_street'),
          SecondaryAddressCity: getFieldValue('secondary_address_city'),
          SecondaryAddressProvinceState: getFieldValue('secondary_address_province_state'),
          SecondaryAddressPostalZip: getFieldValue('secondary_address_postal_zip'),
          SecondaryAddressCountry: getFieldValue('secondary_address_country'),
          FormalNameIfDifferent: getFieldValue('formal_name_if_different'),
          Notes: JSON.stringify(existingNotes),
          RecentActivity: getFieldValue('recent_activity'),
          AssignedLead: getFieldValue('assigned_lead'),
        };

        await ClientService.updateClient(stakeholderId, updateData);
      } else if (agentData) {
        // --- Agent path: Notes may be array or { notes, recent_activity } ---
        const notesField = (agentData as any).Notes ?? (agentData as any).notes;
        let notesArray: any[] = [];
        let recentActivity: any[] = [];

        if (notesField) {
          try {
            const parsed = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
            if (Array.isArray(parsed)) {
              notesArray = parsed;
            } else if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
              notesArray = Array.isArray(parsed.notes) ? parsed.notes : [];
              recentActivity = Array.isArray(parsed.recent_activity) ? parsed.recent_activity : [];
            }
          } catch (e) {
            console.error('Error parsing agent notes:', e);
          }
        }

        const newNote = {
          noteid: nextNoteId(notesArray),
          note_text: noteText.trim(),
          tags: tags,
          created_at: new Date().toISOString(),
          created_by: employeeId,
        };
        notesArray.push(newNote);

        const getAgentField = (key: string) => {
          const v = (agentData as any)[key];
          return v !== null && v !== undefined ? v : '';
        };

        const updatePayload: any = {
          FirstName: getAgentField('FirstName'),
          LastName: getAgentField('LastName'),
          Company: getAgentField('Company'),
          Email: getAgentField('Email'),
          Phone: getAgentField('Phone'),
          Address: getAgentField('Address'),
          Jurisdiction: getAgentField('Jurisdiction'),
          SecondaryPhoneNumber: getAgentField('SecondaryPhoneNumber'),
          Entity: getAgentField('Entity'),
          Title: getAgentField('Title'),
          ContactCreatedAt: getAgentField('ContactCreatedAt'),
          PrimaryAddressCity: getAgentField('PrimaryAddressCity'),
          PrimaryAddressProvince: getAgentField('PrimaryAddressProvince'),
          PrimaryAddressPostal: getAgentField('PrimaryAddressPostal'),
          PrimaryAddressCountry: getAgentField('PrimaryAddressCountry'),
          SecondaryEmail: getAgentField('SecondaryEmail'),
          SecondaryAddressStreet: getAgentField('SecondaryAddressStreet'),
          SecondaryAddressCity: getAgentField('SecondaryAddressCity'),
          SecondaryAddressProvince: getAgentField('SecondaryAddressProvince'),
          SecondaryAddressPostal: getAgentField('SecondaryAddressPostal'),
          SecondaryAddressCountry: getAgentField('SecondaryAddressCountry'),
          ContactType: getAgentField('ContactType'),
          FormalName: getAgentField('FormalName'),
          Notes: JSON.stringify({ notes: notesArray, recent_activity: recentActivity }),
          ContactName: getAgentField('ContactName'),
          MiddleName: getAgentField('MiddleName'),
          DateOfBirth: getAgentField('DateOfBirth'),
        };

        await AgentsService.updateAgent(stakeholderId, updatePayload);
      } else {
        setError('Stakeholder not found.');
        setLoading(false);
        return;
      }

      toastService.success('Note added successfully');

      if (onNoteAdded) {
        onNoteAdded();
      }

      handleClose();
    } catch (error: any) {
      console.error('Error adding note:', error);
      setError(error.message || 'Failed to add the note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-note-overlay" onClick={handleClose}>
      <div className="add-note-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="add-note-header">
          <div className="add-note-header-left">
            <div className="add-note-title-row">
              <div className="add-note-header-icon">
                <FontAwesomeIcon icon={faPlus} />
              </div>
              <div className="add-note-header-text">
                <h2 className="add-note-title">Add Note</h2>
                <p className="add-note-subtitle">Add a note for this {type === 'organization' ? 'organization' : 'stakeholder'}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="add-note-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="add-note-content">
          {/* Note Section */}
          <div className="add-note-section add-note-section-blue">
            <div className="add-note-section-header">
              <div className="add-note-section-icon-wrapper add-note-icon-blue">
                <FontAwesomeIcon icon={faStickyNote} />
              </div>
              <h3 className="add-note-section-title">Note</h3>
            </div>
            <div className="add-note-section-content">
              <div className="add-note-textarea-wrapper">
                <textarea
                  value={noteText}
                  onChange={handleTextChange}
                  placeholder="Type your note here..."
                  rows={8}
                  className="add-note-textarea"
                  maxLength={2000}
                />
                <div className="add-note-char-counter">
                  {noteText.length}/2000
                </div>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          <div className="add-note-section add-note-section-purple">
            <div className="add-note-section-header">
              <div className="add-note-section-icon-wrapper add-note-icon-purple">
                <FontAwesomeIcon icon={faTag} />
              </div>
              <h3 className="add-note-section-title">Tags</h3>
            </div>
            <div className="add-note-section-content">
              <div className="add-note-tag-input-wrapper">
                <label className="add-note-tag-label">Add Tag</label>
                <div className="add-note-tag-input-container">
                  <input
                    type="text"
                    name="tagInput"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Type a tag and press Enter"
                    className="add-note-tag-input"
                    autoComplete="off"
                  />
                  {/* Suggestions dropdown */}
                  {tagInput.trim() && tagSuggestions.length > 0 && (
                    <div className="add-note-tag-suggestions">
                      {tagSuggestions.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="add-note-tag-suggestion-item"
                          onClick={() => addTagFromSuggestion(tag)}
                        >
                          <FontAwesomeIcon icon={faTag} className="add-note-suggestion-icon" />
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected tags */}
              {tags.length > 0 && (
                <div className="add-note-tags-preview">
                  <span className="add-note-tags-label">Selected:</span>
                  {tags.map((tag) => (
                    <span key={tag} className="add-note-tag-preview add-note-tag-selected">
                      {tag}
                      <button
                        type="button"
                        className="add-note-remove-tag"
                        onClick={() => removeTag(tag)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Available tags for quick selection */}
              {availableTags.length > 0 && (
                <div className="add-note-available-tags">
                  <span className="add-note-tags-label">Available tags:</span>
                  <div className="add-note-tags-list">
                    {availableTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="add-note-tag-available"
                        onClick={() => addTagFromSuggestion(tag)}
                        title={`Add "${tag}" tag`}
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="add-note-info-box">
                <FontAwesomeIcon icon={faInfoCircle} className="add-note-info-icon" />
                <span>
                  {existingTags.length > 0 
                    ? 'Click an available tag to add it, or type a new tag and press Enter.'
                    : 'Add tags to categorize your note. Press Enter to add a tag.'
                  }
                </span>
              </div>
            </div>
          </div>

          {error && <div className="add-note-error">{error}</div>}
        </div>

        {/* Footer Actions */}
        <div className="add-note-footer">
          <div className="add-note-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="add-note-footer-info-icon" />
            <span>Note will be saved and visible to your team</span>
          </div>
          <div className="add-note-footer-buttons">
            <button
              type="button"
              className="add-note-button add-note-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="add-note-button add-note-button-primary"
              onClick={handleNoteCreation}
              disabled={loading || !noteText.trim()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Adding...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Add Note
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddNotePage;

